import { Button, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import {
  planWithCadenceLabel,
  useCatalogTierDisplayNames,
  cardLabel,
  formatPlanDate,
  scheduledLineCopy,
} from './lib/helpers'
import {
  RECEIPT_SHELL,
  ReceiptCallout,
  ReceiptCardBody,
  ReceiptHeaderRow,
  ReceiptRowGroup,
  type ReceiptRow,
} from './ReceiptParts'
import type { AppliedPlanChange } from './lib/planChange.types'

export interface ScheduledReceiptProps {
  result: AppliedPlanChange
  onDone: () => void
}

export const ScheduledReceipt = ({ result, onDone }: ScheduledReceiptProps) => {
  const names = useCatalogTierDisplayNames()
  const { toTier, toCadence } = result
  const card = cardLabel(result.cardBrand, result.cardLast4)
  const effectiveDate = formatPlanDate(result.effectiveAt)

  const rows: ReceiptRow[] = []
  if (toTier && toCadence) {
    rows.push({
      testId: 'receipt-row-plan',
      label: 'Plan changes to',
      value: planWithCadenceLabel(names[toTier], toCadence),
    })
  }
  rows.push({
    testId: 'receipt-row-effective',
    label: 'Effective date',
    value: effectiveDate,
  })
  rows.push({
    testId: 'receipt-row-due-today',
    label: 'Amount due today',
    value: formatCurrencyFromCents(result.invoicedAmountCents),
  })
  if (card) {
    rows.push({
      testId: 'receipt-row-card',
      label: 'Card on file',
      value: card,
    })
  }

  return (
    <div className="mx-auto flex w-full max-w-[560px] flex-col gap-5">
      <Text
        variant={TypographyStyles.EYEBROW}
        className="text-neutral-500 hidden md:block"
      >
        Plan change
      </Text>

      <div className="flex flex-col gap-2">
        <Text
          variant={TypographyStyles.DISPLAY_SM}
          className="md:text-[32px]"
          role="heading"
          aria-level={1}
        >
          Your plan change is scheduled
        </Text>
        <Text variant={TypographyStyles.BODY_MD} className="text-neutral-600">
          {`Your plan switches on ${effectiveDate}.`}
        </Text>
      </div>

      <div className={RECEIPT_SHELL}>
        <ReceiptHeaderRow createdAt={result.createdAt} />
        <ReceiptCardBody>
          <ReceiptCallout
            tone="yellow"
            headline={scheduledLineCopy(result.effectiveAt)}
            subline={
              card
                ? 'Your card on file is charged automatically then.'
                : undefined
            }
          />
          <ReceiptRowGroup rows={rows} />
        </ReceiptCardBody>
      </div>

      <Button className="w-full md:w-auto h-11 md:h-10" onClick={onDone}>
        Done
      </Button>
    </div>
  )
}
